import React, { useRef, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Divider,
  LinearProgress,
  Tooltip,
  IconButton,
  Alert,
} from "@mui/material";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import CropIcon from "@mui/icons-material/Crop";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import CancelIcon from "@mui/icons-material/Cancel";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FlipIcon from "@mui/icons-material/Flip";

/**
 * ImageCropDialog — crops a single image in a 1:1 (square) ratio.
 *
 * Props
 * ─────
 * open          : boolean
 * initialSrc    : string | null   — blob / data-URL to crop
 * loading       : boolean         — true while src is being prepared externally
 * isRecrop      : boolean         — true when re-cropping an existing image
 * queueIndex    : number | null   — 0‑based position in batch
 * queueTotal    : number | null   — total images in batch
 * onSave(croppedFile: File)
 * onSkip()
 * onCancelAll()
 * onExited()    — called after dialog exit animation (for cleanup)
 */
const ImageCropDialog = ({
  open,
  initialSrc,
  loading = false,
  isRecrop = false,
  queueIndex = null,
  queueTotal = null,
  onSave,
  onSkip,
  onCancelAll,
  onExited, // new prop for cleanup
}) => {
  const cropperRef = useRef(null);
  const flipXRef = useRef(1);

  const [cropperReady, setCropperReady] = useState(false);
  const [dialogEntered, setDialogEntered] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cropError, setCropError] = useState(""); // user‑visible error

  // Reset state when source changes
  useEffect(() => {
    setCropperReady(false);
    setCropError("");
    flipXRef.current = 1;
  }, [initialSrc]);

  // Timeout: if cropper doesn't become ready after 10s, show error
  useEffect(() => {
    if (!dialogEntered || !initialSrc || loading) return;
    const timer = setTimeout(() => {
      if (!cropperReady) {
        setCropError("Image failed to load. Please try again.");
      }
    }, 10000);
    return () => clearTimeout(timer);
  }, [dialogEntered, initialSrc, loading, cropperReady]);

  // Full teardown when dialog closes
  useEffect(() => {
    if (!open) {
      setCropperReady(false);
      setDialogEntered(false);
      setCropError("");
      flipXRef.current = 1;
    }
  }, [open]);

  const getCropper = () => cropperRef.current?.cropper ?? null;

  const rotate = (deg) => getCropper()?.rotate(deg);
  const zoom = (ratio) => getCropper()?.zoom(ratio);
  const flipH = () => {
    const c = getCropper();
    if (!c) return;
    flipXRef.current *= -1;
    c.scaleX(flipXRef.current);
  };

  const handleSave = () => {
    const c = getCropper();
    if (!c) return;
    setSaving(true);
    setCropError("");
    try {
      const canvas = c.getCroppedCanvas({
        width: 800,
        height: 800,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: "high",
      });
      if (!canvas) {
        setCropError("Failed to generate cropped image.");
        setSaving(false);
        return;
      }
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            setCropError("Failed to create image file.");
            setSaving(false);
            return;
          }
          const file = new File([blob], `cropped_${Date.now()}.jpg`, {
            type: "image/jpeg",
          });
          setSaving(false);
          onSave?.(file);
        },
        "image/jpeg",
        0.92
      );
    } catch (err) {
      console.error("Crop error:", err);
      setCropError(`Cropping error: ${err.message}`);
      setSaving(false);
    }
  };

  const handleSkip = () => onSkip?.();
  const handleCancelAll = () => onCancelAll?.();

  const isBatch = queueTotal !== null && queueTotal > 1;
  const currentNum = queueIndex !== null ? queueIndex + 1 : null;
  const progressPct =
    isBatch && currentNum
      ? Math.round(((currentNum - 1) / queueTotal) * 100)
      : 0;

  const dialogTitle = isRecrop
    ? "Recrop Image"
    : isBatch
    ? `Crop Image ${currentNum} of ${queueTotal}`
    : "Crop Image";

  const showCropper = dialogEntered && !loading && !!initialSrc;
  const showPlaceholder = !showCropper;

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      keepMounted={false}
      onClose={handleCancelAll}
      PaperProps={{ sx: { borderRadius: 3 } }}
      TransitionProps={{
        onEntered: () => setDialogEntered(true),
        onExited: onExited, // call cleanup after exit
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CropIcon color="primary" />
          <Typography variant="h6" component="span" fontWeight={700}>
            {dialogTitle}
          </Typography>
          {isBatch && (
            <Chip
              label={`${currentNum} / ${queueTotal}`}
              size="small"
              color="primary"
              sx={{ ml: "auto" }}
            />
          )}
        </Box>
        {isBatch && (
          <Box sx={{ mt: 1.5 }}>
            <LinearProgress
              variant="determinate"
              value={progressPct}
              sx={{ height: 6, borderRadius: 1 }}
            />
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              {currentNum - 1} of {queueTotal} cropped
            </Typography>
          </Box>
        )}
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 2, overflow: "hidden" }}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: 380,
            bgcolor: "grey.900",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {showPlaceholder && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
              }}
            >
              <CircularProgress sx={{ color: "white" }} size={40} />
              <Typography variant="body2" sx={{ color: "grey.300" }}>
                {loading ? "Loading image…" : "Preparing cropper…"}
              </Typography>
            </Box>
          )}

          {showCropper && (
            <>
              {!cropperReady && !cropError && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(0,0,0,0.55)",
                    zIndex: 10,
                    gap: 1,
                  }}
                >
                  <CircularProgress sx={{ color: "white" }} size={36} />
                  <Typography variant="caption" sx={{ color: "white" }}>
                    Loading image…
                  </Typography>
                </Box>
              )}

              <Cropper
                key={initialSrc}
                ref={cropperRef}
                src={initialSrc}
                style={{ display: "block", width: "100%", height: "100%" }}
                aspectRatio={1}
                viewMode={1}
                dragMode="move"
                autoCropArea={0.95}
                responsive={true}
                restore={false}
                checkOrientation={false}
                background={false}
                guides={true}
                center={true}
                highlight={false}
                cropBoxMovable={true}
                cropBoxResizable={true}
                toggleDragModeOnDblclick={false}
                ready={() => setCropperReady(true)}
              />
            </>
          )}
        </Box>

        {/* Error message */}
        {cropError && (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
            onClose={() => setCropError("")}
          >
            {cropError}
          </Alert>
        )}

        {cropperReady && showCropper && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 0.5,
              mt: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Tooltip title="Rotate left 90°">
              <IconButton size="small" onClick={() => rotate(-90)}>
                <RotateLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Rotate right 90°">
              <IconButton size="small" onClick={() => rotate(90)}>
                <RotateRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Flip horizontal">
              <IconButton size="small" onClick={flipH}>
                <FlipIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Tooltip title="Zoom in">
              <IconButton size="small" onClick={() => zoom(0.1)}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Zoom out">
              <IconButton size="small" onClick={() => zoom(-0.1)}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {showCropper && (
          <Typography
            variant="caption"
            color="textSecondary"
            align="center"
            sx={{ display: "block", mt: 1 }}
          >
            Drag to reposition · Resize handles to adjust · Saved as 800×800
            square
          </Typography>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 2, py: 1.5, gap: 1, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          color="error"
          startIcon={<CancelIcon />}
          onClick={handleCancelAll}
          disabled={saving}
          sx={{ mr: "auto" }}
        >
          {isBatch && !isRecrop ? "Cancel All" : "Cancel"}
        </Button>

        {isBatch && !isRecrop && (
          <Tooltip title="Skip this image — continue to the next">
            <span>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<SkipNextIcon />}
                onClick={handleSkip}
                disabled={saving}
              >
                Skip
              </Button>
            </span>
          </Tooltip>
        )}

        <Button
          variant="contained"
          color="primary"
          startIcon={
            saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <CropIcon />
            )
          }
          onClick={handleSave}
          disabled={saving || !cropperReady || !showCropper}
        >
          {saving
            ? "Saving…"
            : isRecrop
            ? "Apply Crop"
            : isBatch && currentNum < queueTotal
            ? "Crop & Next"
            : "Crop & Done"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropDialog;
